import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { extractPrefixedTeamId } from "@smart/bridge-types-basic";
import {
  FieldList,
  Modal,
  Select,
  TextFieldInput,
} from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { buildDisplayName } from "@smart/itops-utils-basic";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

import type { GqlSubmissionLinkHash } from "../edit-form-v2/types";

export const webEnquiryLinkFormSchema = z.object({
  linkLabel: z.string().trim().min(1, "Please enter a link label"),
  staffIds: z
    .array(z.string())
    .min(1, "Please select at least one staff member to notify"),
  gaMeasurementId: z.string(),
});

export type WebEnquiryLinkFormValues = z.infer<typeof webEnquiryLinkFormSchema>;

type StaffOption = { label: string; value: string };

const getDefaultValues = (
  submissionLinkHash?: GqlSubmissionLinkHash | null,
): WebEnquiryLinkFormValues => {
  if (!submissionLinkHash) {
    return {
      linkLabel: "",
      staffIds: [],
      gaMeasurementId: "",
    };
  }

  const staffIds =
    submissionLinkHash.notifiedUserUris?.flatMap((userUri) => {
      const { id } = extractPrefixedTeamId(userUri, "sb");
      return id ? [id] : [];
    }) ?? [];

  const gaMeasurementId =
    submissionLinkHash.analyticsTools?.find(
      (tool) => tool.type === "googleAnalytics",
    )?.trackingId ?? "";

  return {
    linkLabel: submissionLinkHash.label?.trim() ?? "",
    staffIds,
    gaMeasurementId,
  };
};

export type WebEnquiryLinkModalProps = {
  open: boolean;
  onClose: () => void;
  formTitle: string;
  submissionLinkHash?: GqlSubmissionLinkHash;
  loading?: boolean;
  onSubmit: (options: {
    uri?: string;
    values: WebEnquiryLinkFormValues;
  }) => void | Promise<void>;
};

export const WebEnquiryLinkModal = ({
  open,
  onClose,
  formTitle,
  submissionLinkHash,
  loading: loadingProp,
  onSubmit,
}: WebEnquiryLinkModalProps) => {
  const { staff } = useSmokeballApp();
  const { publishEvent } = useAnalyticsEvent();

  const staffOptions: StaffOption[] = useMemo(
    () =>
      staff?.current
        ?.filter((s) => s.enabled)
        .map((s) => ({
          label: buildDisplayName(s, s.userId),
          value: s.userId,
        })) ?? [],
    [staff?.current],
  );

  const loading = !!loadingProp || !!staff?.loading;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<WebEnquiryLinkFormValues>({
    defaultValues: getDefaultValues(submissionLinkHash),
    resolver: zodResolver(webEnquiryLinkFormSchema),
  });

  useEffect(() => {
    if (!open) return;
    reset(getDefaultValues(submissionLinkHash));
  }, [open, submissionLinkHash, reset]);

  const isEdit = !!submissionLinkHash;
  const headerText = isEdit ? "Link and QR Code" : "Create Link and QR Code";
  const submitButtonText = isEdit ? "Save" : "Create Link and QR Code";
  const descriptionText = isEdit
    ? "Edit the link and QR code"
    : "Create a new link and QR code for this form to enable sharing";

  const onFormSubmit = async (values: WebEnquiryLinkFormValues) => {
    await onSubmit({
      uri: submissionLinkHash?.uri,
      values,
    });

    if (!isEdit) {
      publishEvent?.({
        category: "track",
        name: "IntakeShareLinkCreated",
        screen: "FormBuilder",
        entryPoint: "IntakeFormBuilder",
        component: "ShareTabCreateLinkandQRCode",
        jiraKey: "LINK-3413",
        interactionType: "Active",
        properties: {
          FormName: formTitle,
        },
      }).catch(console.error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
      <Modal
        dataTestId="web-enquiry-link-modal"
        header={{
          text: headerText,
          icon: "solidGlobe",
          iconBgColor: "bg-orange-200",
        }}
        open={open}
        onClose={onClose}
        loading={loading || isSubmitting}
        footer={{
          buttons: [
            {
              text: "Cancel",
              variant: "secondarySubtle",
              type: "button",
              onClick: onClose,
            },
            {
              text: submitButtonText,
              variant: "primaryDefault",
              type: "submit",
              disabled: loading || isSubmitting,
            },
          ],
        }}
      >
        <div className="flex flex-col gap-medium text-neutral-850">
          <p>{descriptionText}</p>
          <FieldList>
            <TextFieldInput
              id="web-enquiry-link-form-name"
              label="Form Name"
              value={formTitle}
              disabled
              readOnly
              onChange={() => {}}
            />
            <Controller
              control={control}
              name="linkLabel"
              render={({ field, fieldState }) => (
                <TextFieldInput
                  id={field.name}
                  label="Link Label (Internal)"
                  mandatory
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  message={
                    fieldState.error?.message ??
                    "Create a unique name so your team knows where this link is used (e.g. Website, business card)"
                  }
                  error={!!fieldState.error?.message}
                  dataTestId="web-enquiry-link-label"
                />
              )}
            />
            <Controller
              control={control}
              name="staffIds"
              render={({ field, fieldState }) => (
                <Select
                  id={field.name}
                  name={field.name}
                  label="Staff to notify when a form is submitted"
                  mandatory
                  options={staffOptions}
                  value={field.value}
                  onChange={(vals) => {
                    let next: string[] = [];
                    if (Array.isArray(vals)) next = vals;
                    else if (vals) next = [vals];
                    field.onChange(next);
                  }}
                  valueComparer={(a, b) => a === b}
                  isMultiple
                  message={fieldState.error?.message}
                  error={!!fieldState.error?.message}
                  empty="No staff available"
                  dataTestId="web-enquiry-link-staff"
                />
              )}
            />
            <div className="flex flex-col gap-medium border-t border-solid border-neutral-100 pt-[1.6rem]">
              <div className="flex flex-col gap-xSmall">
                <h4 className="text-paragraph font-semibold">
                  Analytics Tracking
                </h4>
                <p>
                  Track views and submissions for this web enquiry form using
                  Google Analytics.
                </p>
              </div>
              <Controller
                control={control}
                name="gaMeasurementId"
                render={({ field, fieldState }) => (
                  <TextFieldInput
                    id={field.name}
                    label="Google Analytics Measurement ID"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    message={
                      fieldState.error?.message ??
                      "Your GA 4 ID (e.g., G-XXXXXXXX); Found in Google Analytics > Admin > Data Streams"
                    }
                    error={!!fieldState.error?.message}
                    dataTestId="web-enquiry-link-ga-measurement-id"
                  />
                )}
              />
            </div>
          </FieldList>
        </div>
      </Modal>
    </form>
  );
};
